"use client";

/**
 * The identity switcher — which client, which program, which workspace.
 *
 * The app ships one of these in its sidebar (`components/workspace/
 * WorkspaceSwitcher.tsx`): a 24px initial tile, the name, a mono caption
 * under it, a pair of chevrons, and a searchable list of initials and names.
 * It is the most-looked-at control in the product and it exists exactly once,
 * hand-built, on shadcn's `Command` — so the second surface that needed one
 * (NewForm One's program picker) built a different thing, which is where a
 * design system loses.
 *
 * WHY IT IS NOT A COMBOBOX, since the surface already has one. A combobox is a
 * *field*: it answers "what value does this input hold", sits in a form, and
 * looks like the input next to it. This answers "whose data am I looking at" —
 * it is chrome, it lives in a rail rather than a form, and its trigger is an
 * identity tile rather than a field. Same popover, same list, same keyboard;
 * a different question, so a different trigger.
 *
 * What it does keep from `Combobox`, deliberately: the filter appears only
 * past a threshold, focus lands in the filter when there is one and on the
 * list when there is not, the list is a `listbox` with `option` rows, and
 * dismiss is outside-click and Escape via `useDismiss` rather than a focus
 * trap — it is a popover, and the work behind it continues.
 *
 * KNOWN DEBT, stated rather than hidden: the keyboard handling below mirrors
 * `Combobox`'s instead of sharing it, because that component owns its trigger,
 * popover and keyboard as one unit. The right end state is one hook holding
 * the list-and-keyboard behaviour with two triggers over it. Until then these
 * two have to be changed together, and this comment is the only thing saying
 * so — which is the same redundancy `SearchList` is already flagged for on
 * `/prototypes/pickers`.
 */

import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { fuzzyRank } from "./fuzzy";
import {
  CONTROL_PRESS,
  FIELD_BASE,
  FIELD_FOCUS,
  OVERLAY_SURFACE,
} from "./surface-styles";
import { Body, Hint, Rail, Strong } from "./text";
import { useDismiss } from "./use-overlay";

export type SwitcherOption = {
  id: string;
  name: string;
  /** The line under the name — a payout rate, a slug, a plan. */
  detail?: string;
};

/**
 * Past this many options the filter field appears — which is none of them.
 *
 * This started at seven, on the argument that a field over three items is a
 * control asking you to type what you can already see. The argument is real
 * and it loses to a simpler one: with the field hidden there is nothing on
 * screen that says you may type, so the search is not a feature, it is a
 * secret. Typeahead still works and still cannot be seen — invisible
 * behaviour is a bonus for the person who guesses, never the only route in.
 *
 * So the field is there by default and the threshold stays a prop, for the
 * caller who has three options and wants the panel to be three rows:
 * `filterAfter={6}` restores the old behaviour, and typing on the list still
 * finds things.
 */
const FILTER_AFTER = 0;

/** How long a typed run stays open before the next keystroke starts over. */
const TYPEAHEAD_MS = 800;

export function Switcher({
  value,
  onChange,
  options,
  /** The mono caption under the name: what kind of thing this switches. */
  label,
  searchPlaceholder = "Search…",
  searchLabel,
  filterAfter = FILTER_AFTER,
  fill = "pane",
  side = "top",
  compact = false,
}: {
  value: string;
  onChange: (id: string) => void;
  options: SwitcherOption[];
  label: string;
  searchPlaceholder?: string;
  /**
   * The filter's accessible name. Defaults to "Search <label>".
   *
   * A placeholder is not a name: it is a hint, it is not announced as a label
   * by every reader, and it disappears the moment someone types. `Combobox`
   * takes the same prop for the same reason.
   */
  searchLabel?: string;
  /**
   * How many options before the filter field is drawn. 0 — the default —
   * always draws it. Typeahead on the list works either side of the line.
   */
  filterAfter?: number;
  /**
   * What the trigger sits on, because a fill is only ever right against its
   * own background.
   *
   * `pane` (default) — a white pane, for a rail or a page whose ground the
   * trigger has to lift off. This is where the control actually lives.
   *
   * `field` — the recessed fill and hairline of `fieldStyles`, for a trigger
   * that sits *on* a pane among other controls. On a white card the pane fill
   * is invisible; this is the treatment every other control on that card
   * already has, so the switcher reads as one of them.
   */
  fill?: "pane" | "field";
  /**
   * Which way the list opens. `top` (default) for a trigger at the foot of a
   * column, where a panel opening downward has nowhere to go; `bottom` for one
   * at the head of it.
   */
  side?: "top" | "bottom";
  /**
   * Only the initial tile, for a rail folded to its icons. The same trigger
   * rather than a second one: the name and chevron fade and the button's own
   * overflow crops them, so the tile never moves and the list still opens
   * from it. The tile is the program's mark, which is what is left to say
   * when there is no room for its name.
   */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const typing = useRef({ run: "", timer: 0 });
  const listId = useId();

  const current = options.find((option) => option.id === value) ?? options[0];
  const filtering = options.length > filterAfter;
  const shown = useMemo(
    () =>
      fuzzyRank(
        query,
        options,
        (option) => option.name,
        (option) => option.detail,
      ),
    [options, query],
  );

  /**
   * Which row Enter will take, named for a screen reader.
   *
   * In the filtered mode focus stays in the field while the arrows move the
   * active row, so the field has to say where that row is — without this a
   * reader lands in an edit box and has no way to know what it is about to
   * choose. Same contract as `Combobox`.
   */
  const activeId = `${listId}-opt-${shown[active]?.id ?? ""}`;

  useDismiss({ open, onClose: () => setOpen(false), ref: root });

  // A run of keystrokes belongs to one opening of the list.
  useEffect(() => {
    if (open) return;
    window.clearTimeout(typing.current.timer);
    typing.current.run = "";
  }, [open]);

  // Opening lands focus where the first keystroke is useful: in the filter
  // when there is one, on the list when there is not.
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    setActive(
      Math.max(
        0,
        shown.findIndex((o) => o.id === current?.id),
      ),
    );
    const target = filtering ? field.current : list.current;
    target?.focus();
    // THE OPEN TRANSITION ONLY.
    //
    // `shown` was in here, and it is a `useMemo` over the query, so it
    // changed on every keystroke and re-ran all of this: the highlight
    // jumped back to wherever the already-selected row landed in the
    // filtered list, undoing the `setActive(0)` that typing had just set,
    // and focus was re-applied mid-word. Filter, press Enter straight
    // away, and you opened the wrong program.
    //
    // Seeding the highlight from the current selection is a question about
    // the moment the panel opens, when the query is empty and `shown` is
    // the whole list. Nothing after that should re-seed it.
  }, [open]);

  function pick(id: string) {
    onChange(id);
    setOpen(false);
  }

  /**
   * Typeahead, for the mode with no field.
   *
   * Keystrokes accumulate for `TYPEAHEAD_MS` and rank the list the same way
   * the field does, so "hex" and "hx" both land on HexClad. It moves the
   * active row rather than filtering the list: with six items or fewer, seeing
   * the others is worth more than hiding them, and the row you are on is the
   * one Enter takes.
   */
  function typeahead(character: string) {
    window.clearTimeout(typing.current.timer);
    typing.current.run += character;
    typing.current.timer = window.setTimeout(() => {
      typing.current.run = "";
    }, TYPEAHEAD_MS);

    const best = fuzzyRank(
      typing.current.run,
      options,
      (option) => option.name,
    )[0];
    if (best) setActive(options.findIndex((o) => o.id === best.id));
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((at) => {
        const next = event.key === "ArrowDown" ? at + 1 : at - 1;
        return Math.min(shown.length - 1, Math.max(0, next));
      });
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActive(shown.length - 1);
      return;
    }
    if (event.key === "Enter" && shown[active]) {
      event.preventDefault();
      pick(shown[active].id);
      return;
    }
    // A single printable character, with no modifier holding it: that is
    // someone typing a name, not reaching for a shortcut.
    if (
      !filtering &&
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      typeahead(event.key);
    }
  }

  return (
    <div ref={root} className="relative" data-popover-open={open || undefined}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.name ?? ""}`}
        onClick={() => setOpen((was) => !was)}
        title={compact ? current?.name : undefined}
        className={cn(
          "flex w-full items-center gap-nf-ds-item overflow-hidden rounded-[12px] py-nf-ds-2 text-left outline-hidden",
          // Folded, the tile centres in the rail's 40px column, on the same
          // line as the nav glyphs below it.
          compact ? "px-nf-ds-2 hover:bg-nf-ds-fg/[0.04]" : "px-nf-ds-3",
          compact
            ? null
            : fill === "pane"
              ? "bg-nf-ds-surface-card"
              : // `FIELD_BASE`'s own treatment, minus the field geometry: this
                // is a row, not a 38px control, and the two should not disagree
                // about which fill a control on a pane takes.
                "bg-nf-ds-surface-ground ring-1 ring-nf-ds-surface-card-ring ring-inset",
          "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
          CONTROL_PRESS,
        )}
      >
        <Initial name={current?.name ?? "?"} current />
        <span
          className={cn(
            "flex min-w-0 flex-1 flex-col gap-[2px] transition-opacity ease-out motion-reduce:transition-none",
            compact ? "opacity-0 duration-100" : "duration-150 delay-75",
          )}
        >
          <Body as="span" className="min-w-0 truncate text-[13px] font-[600]">
            {current?.name}
          </Body>
          {/* Truncating, never wrapping: in a 208-point rail this has about
              130 to work in, and "CLIENT WORKSPACE" at this tracking is right
              at that line — wrapped onto two it stops being a caption. */}
          <Rail className="min-w-0 truncate whitespace-nowrap">{label}</Rail>
        </span>
        <ChevronsUpDown
          aria-hidden="true"
          className={cn(
            "size-[14px] shrink-0 text-muted-foreground",
            compact && "opacity-0",
          )}
          strokeWidth={2}
        />
      </button>

      {open ? (
        <div
          className={cn(
            "absolute left-0 z-50 w-[max(100%,300px)] overflow-hidden",
            side === "top" ? "bottom-[calc(100%+8px)]" : "top-[calc(100%+8px)]",
            OVERLAY_SURFACE,
            "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-[0.98] motion-safe:duration-150",
          )}
          // Grows out of the trigger, whichever edge of it the panel is on.
          style={{
            transformOrigin: `${side === "top" ? "bottom" : "top"} left`,
          }}
        >
          {filtering ? (
            <div className="p-nf-ds-2 pb-nf-ds-1">
              <span className="relative flex items-center">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-nf-ds-3 size-[15px] text-muted-foreground"
                />
                {/* Named, pointed at its list, and saying which row is
                    active: the three attributes `Combobox`'s filter carries.
                    Not `role="combobox"` — that component does not set it
                    either, and a second contract for one pattern is the drift
                    this surface exists to argue against. */}
                <input
                  ref={field}
                  aria-label={searchLabel ?? `Search ${label.toLowerCase()}`}
                  aria-activedescendant={activeId}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={searchPlaceholder}
                  aria-controls={listId}
                  className={cn(
                    FIELD_BASE,
                    FIELD_FOCUS,
                    "h-[38px] w-full rounded-[10px] pr-nf-ds-3 pl-nf-ds-8 font-nf-ds-sans text-[14px]",
                  )}
                />
              </span>
            </div>
          ) : null}

          {/* The ARIA combobox pattern keeps focus on one element and points
              at the active row with aria-activedescendant, so rows carry
              tabIndex={-1} — clickable and reachable by arrow key without
              putting every option into the tab order. The suppression records
              that no native element does this: a <select> can be neither
              filtered nor given two-line rows. Same shape as `Combobox`, on
              purpose. */}
          {/* biome-ignore lint/a11y/useSemanticElements: no native filtered listbox exists */}
          <div
            ref={list}
            id={listId}
            role="listbox"
            // Focusable only in the mode with no filter to hold focus.
            tabIndex={filtering ? undefined : -1}
            aria-activedescendant={filtering ? undefined : activeId}
            onKeyDown={filtering ? undefined : onKeyDown}
            aria-label={label}
            className={cn(
              "flex max-h-[320px] flex-col gap-nf-ds-1 overflow-y-auto p-nf-ds-2 outline-hidden",
              filtering && "pt-0",
            )}
          >
            {shown.length === 0 ? (
              <Hint muted className="block px-nf-ds-3 py-nf-ds-3">
                Nothing matches “{query}”.
              </Hint>
            ) : null}

            {shown.map((option, index) => {
              const selected = option.id === current?.id;
              return (
                // biome-ignore lint/a11y/useSemanticElements: an option row has no native equivalent outside <select>
                <button
                  key={option.id}
                  id={`${listId}-opt-${option.id}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected}
                  onClick={() => pick(option.id)}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    "flex min-h-[52px] w-full items-center gap-nf-ds-item rounded-[10px] px-nf-ds-2 py-nf-ds-2 text-left outline-hidden",
                    index === active && "bg-nf-ds-surface-ground",
                  )}
                >
                  <Initial name={option.name} />
                  {/* One weight and one colour apart, with the detail on its
                      own line: at 13px muted against 13px plain the two read
                      as one run of text, which is what made the rows look
                      like a paragraph in a box. */}
                  <span className="flex min-w-0 flex-1 flex-col gap-[1px]">
                    <Strong as="span" className="min-w-0 truncate">
                      {option.name}
                    </Strong>
                    {option.detail ? (
                      <Hint muted className="min-w-0 truncate">
                        {option.detail}
                      </Hint>
                    ) : null}
                  </span>
                  {selected ? (
                    <Check
                      aria-hidden="true"
                      className="size-[14px] shrink-0 text-nf-ds-brand"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The initial tile.
 *
 * Brand fill for the one in the trigger, a neutral wash for the rows: the
 * trigger is stating who you are and the rows are offering alternatives, and
 * twelve brand-coloured squares in a column would say all of them are.
 */
function Initial({ name, current }: { name: string; current?: boolean }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-[8px] font-nf-ds-sans font-[600]",
        current ? "size-[24px] text-[11px]" : "size-[28px] text-[12px]",
        current
          ? "bg-nf-ds-brand text-nf-ds-white"
          : "bg-nf-ds-fg/[0.07] text-muted-foreground",
      )}
    >
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}
